import { isPlatformBrowser } from "@angular/common";
import { EventEmitter, PLATFORM_ID, ViewContainerRef, } from "@angular/core";
import { TimeUtils } from "@topyourthree/time-utils";
import { fromEvent, Subscription } from "rxjs";
import * as i0 from "@angular/core";
import * as i1 from "@topyourthree/time-utils";
import * as i2 from "@topyourthree/directives";
const _c0 = ["*"];
export class CalloutTriggerComponent {
    constructor(platformId, containerRef, timeUtils) {
        this.platformId = platformId;
        this.containerRef = containerRef;
        this.timeUtils = timeUtils;
        this.trigger = new EventEmitter();
        this.open = new EventEmitter();
        this.close = new EventEmitter();
        this.disableMobile = false;
        this.disabled = false;
        this.isHoverDisabled = false;
        this.scrollSubscription = new Subscription();
        /**
         * To prevent the callout to open when the user is scrolling, we need to check if the user is
         * still scrolling after 500ms. This timer is used to check if the user is still scrolling.
         *
         * @private
         */
        this.scrollTimerId = undefined;
        /**
         * Handles the scroll event by setting the scroll position, disabling hover, and delaying further checks for 500ms.
         * If the scroll position changes within that time, hover will remain disabled and the delay will reset.
         * Otherwise, hover will be enabled and wait for a new scroll event to fire.
         *
         * @returns {void}
         * @internal
         *
         * NOTE: Reason for defining this as an arrow function is to prevent the need to bind this to the function
         */
        this.onScroll = () => {
            this.scrollPos = window.scrollY;
            this.isHoverDisabled = true;
            if (this.scrollTimerId !== undefined) {
                clearTimeout(this.scrollTimerId);
            }
            // Delay checking scroll position to prevent multiple scroll events
            this.scrollTimerId = this.timeUtils.setTimeout(() => {
                /**
                 * Check if scroll position changed after 500ms
                 * If true, keep hover disabled and check again after 500ms
                 * Else, enable hover and wait for a new scroll event to fire
                 */
                const scrollPosChanged = this.scrollPos !== window.scrollY;
                this.isHoverDisabled = scrollPosChanged;
                this.disableHoverWhileScrolling(scrollPosChanged);
                this.scrollTimerId = undefined;
            }, 500);
        };
    }
    /**
     * Reason for exposing the nativeElement is to allow the parent component to
     * pass the nativeElement to the callout content component to prevent emitting
     * outsideClick event after clicking the trigger.
     */
    get nativeElement() {
        return this.containerRef.element.nativeElement;
    }
    handleTriggerClick(event) {
        /**
         * Force open if hover is set to true
         * to support touch devices as well.
         *
         * Note: Clicking the trigger again or outside
         * the callout will close the callout.
         */
        if (this.disabled) {
            return;
        }
        if (this.isMobileViewport() && this.disableMobile) {
            return;
        }
        if (!this.isMobileViewport() && this.hover) {
            this.open.emit({ isPinnedOnClick: true });
            return;
        }
        if (this.isMobileViewport() &&
            this.mobileLongPress &&
            !this.mobileForceClick) {
            return;
        }
        this.trigger.emit(event);
    }
    handleLongPressedMobile() {
        if (!this.mobileLongPress) {
            return;
        }
        this.open.emit();
    }
    handleTriggerMouseover() {
        // Prevent this from running on touch device as it suppress
        // click event from triggering
        if (!this.isMobileViewport() &&
            !this.isTouchDevice() &&
            !this.isHoverDisabled) {
            this.open.emit();
        }
    }
    handleTriggerMouseout() {
        if (!this.isMobileViewport() && !this.isTouchDevice()) {
            this.close.emit();
        }
    }
    getElementBoundingRect() {
        return this.nativeElement.getBoundingClientRect();
    }
    setOptions(options) {
        this.hover = options.hover;
        this.mobileForceClick = options.mobileForceClick;
        this.mobileLongPress = options.mobileLongPress;
    }
    ngOnInit() {
        if (this.hover && isPlatformBrowser(this.platformId)) {
            this.disableHoverWhileScrolling();
        }
    }
    isMobileViewport() {
        return false;
        return (window.innerWidth < parseInt("0px" /* TopYourThreeBreakpointValues.TABLET */, 10));
    }
    isTouchDevice() {
        return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    }
    /**
     * Removes the scroll event listener.
     *
     * @returns {void}
     */
    destroyScrollListener() {
        if (this.hover && isPlatformBrowser(this.platformId)) {
            this.scrollSubscription.unsubscribe();
        }
    }
    /**
     * Disables hover while the user is scrolling by removing the scroll event listener,
     * adding it back once with the "once" option to listen for the next scroll event,
     * and/or immediately invoking the onScroll handler if the scroll position has already changed.
     *
     * @param {boolean} [scrollCheckRetry=false] - Indicates whether to immediately invoke the onScroll handler.
     * @returns {void}
     */
    disableHoverWhileScrolling(scrollCheckRetry = false) {
        this.destroyScrollListener();
        if (!scrollCheckRetry) {
            // eslint-disable-next-line local-rules/no-scroll-listener
            this.scrollSubscription = fromEvent(window, "scroll", {
                once: true,
            }).subscribe(this.onScroll);
        }
        else {
            this.onScroll();
        }
    }
    ngOnDestroy() {
        if (this.scrollTimerId !== undefined) {
            clearTimeout(this.scrollTimerId);
        }
        this.destroyScrollListener();
    }
}
CalloutTriggerComponent.ɵfac = function CalloutTriggerComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || CalloutTriggerComponent)(i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i1.TimeUtils)); };
CalloutTriggerComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: CalloutTriggerComponent, selectors: [["fl-callout-trigger"]], hostVars: 1, hostBindings: function CalloutTriggerComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function CalloutTriggerComponent_click_HostBindingHandler($event) { return ctx.handleTriggerClick($event); })("handleLongPressedMobile", function CalloutTriggerComponent_handleLongPressedMobile_HostBindingHandler() { return ctx.handleLongPressedMobile(); })("mouseenter", function CalloutTriggerComponent_mouseenter_HostBindingHandler() { return ctx.handleTriggerMouseover(); })("mouseleave", function CalloutTriggerComponent_mouseleave_HostBindingHandler() { return ctx.handleTriggerMouseout(); });
    } if (rf & 2) {
        i0.ɵɵattribute("data-mobile-force-click", ctx.mobileForceClick);
    } }, inputs: { disableMobile: "disableMobile", disabled: "disabled" }, outputs: { trigger: "trigger", open: "open", close: "close" }, features: [i0.ɵɵHostDirectivesFeature([{ directive: i2.LongPressDirective, outputs: ["longPressedMobile", "handleLongPressedMobile"] }])], ngContentSelectors: _c0, decls: 1, vars: 0, template: function CalloutTriggerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵprojection(0);
    } }, styles: ["[_nghost-%COMP%]{display:inline-block;position:relative;cursor:pointer;-webkit-tap-highlight-color:rgba(0,0,0,0)}[data-mobile-force-click=true][_nghost-%COMP%]{cursor:pointer}.Disabled[_nghost-%COMP%], [disabled][_nghost-%COMP%]{pointer-events:none;opacity:.4;cursor:default}[_nghost-%COMP%]:hover[data-mobile-force-click=false]   .TriggerHoverHighlight[_ngcontent-%COMP%]{opacity:1}  .TriggerContent{display:inline-flex;align-items:center}.TriggerHoverHighlight[_ngcontent-%COMP%]{position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.05);border-radius:4px;opacity:0;transition:opacity .2s ease;pointer-events:none}@media(max-width: 768px){[_nghost-%COMP%]   .TriggerHoverHighlight[_ngcontent-%COMP%]{display:none}}\n/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"], changeDetection: 0 });
